import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { GroupFilter } from 'types/groupService';
import { GROUPS_FILTERS_TITLES } from 'global/utils/DashboardFilters';
import { Checkbox, InputText, Select, SwissSelect } from '@insurancemenu/imenu-components-lib';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { useBiDashboardPageStore } from 'store/pages/bi-groups-dashboard/pageStore';
import { debounce } from 'global/utils/debounce';
import { AdditionalFiltersSectionStyled, FilterSectionStyled, FiltersWrapperStyled } from './styles';

function getEffectiveDateOptions(): OptionType[] {
  const now = new Date();
  now.setDate(1);
  // Date Range: 3 months before, current month, and 12 months after the current month
  const options = Array.from({ length: 16 }).map((_, index) => {
    const date = new Date(now);
    date.setMonth(date.getMonth() - 3 + index);
    const dateValue = date.toISOString().slice(0, 10);
    return {
      value: dateValue,
      label: dateFormatters.normDateToLongAmericanDate(dateValue),
    };
  });

  return [{ label: 'All', value: '' }, ...options];
}

const FiltersPanel: React.FC = observer(() => {
  const {
    paginationDelegate: { filters, setFilterValue },
    groupFilters,
    filterAllGroupsAndConnections,
    searchAgencyDelegate,
    availableStatesOptions,
  } = useBiDashboardPageStore();

  const onSetActiveFilter = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setFilterValue('filter', e.target.value as GroupFilter);
  };

  const onSetAgency = (value: string[]): void => {
    setFilterValue('agencyId', value[0] ?? '');
  };

  const onSetEffectiveDate = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setFilterValue('effectiveDate', e.target.value);
  };
  const onSetState = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setFilterValue('state', e.target.value);
  };

  const onUpdateAssignedGroups = (): void => {
    setFilterValue('assignedGroups', !filters.assignedGroups);
  };

  const onUpdateCreatedBy = (): void => {
    setFilterValue('createdByMe', !filters.createdByMe);
  };

  const onUpdateAll = (): void => {
    setFilterValue('allGroupsAndConnections', !filterAllGroupsAndConnections);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        setFilterValue('carrierGroupNumber', searchValue);
      }, 300),
    [setFilterValue],
  );

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedSearch(e.target.value);
    },
    [debouncedSearch],
  );

  const agencyOptions: OptionType[] = [
    { label: 'All', value: '' },
    ...searchAgencyDelegate.allSponsorAgencies.map((agency) => ({
      label: agency.name,
      value: agency.id,
    })),
  ];

  const stageOptions: OptionType[] = groupFilters.map((filterKey) => ({
    label: GROUPS_FILTERS_TITLES[filterKey],
    value: filterKey,
  }));

  return (
    <FiltersWrapperStyled>
      <FilterSectionStyled>
        <Select
          name="stage"
          label="Pipeline Stage"
          options={stageOptions}
          value={filters.filter}
          onChange={onSetActiveFilter}
        />
        <Select
          name="effectiveDate"
          label="Effective Date"
          options={getEffectiveDateOptions()}
          onChange={onSetEffectiveDate}
          value={filters.effectiveDate}
          placeholder="Search by date"
        />
      </FilterSectionStyled>
      <SwissSelect
        id="agency"
        label="Agency"
        options={agencyOptions}
        value={filters.agencyId ? [filters.agencyId] : []}
        onChange={onSetAgency}
        placeholder="All agencies"
        withSearch
        searchPlaceholder="Agency name"
      />
      <FilterSectionStyled>
        <Select
          name="state"
          label="State"
          options={availableStatesOptions}
          value={filters.state}
          onChange={onSetState}
          placeholder="All states"
        />
        <InputText
          name="carrierGroupNumber"
          htmlType="search"
          label="Carrier Group Number"
          placeholder="Carrier group number"
          onChange={handleSearch}
        />
      </FilterSectionStyled>
      <AdditionalFiltersSectionStyled>
        <Checkbox label="All" checked={filterAllGroupsAndConnections} onChange={onUpdateAll} />
        <Checkbox label="My Assignments" checked={filters.assignedGroups} onChange={onUpdateAssignedGroups} />
        <Checkbox label="Created by Me" checked={filters.createdByMe} onChange={onUpdateCreatedBy} />
      </AdditionalFiltersSectionStyled>
    </FiltersWrapperStyled>
  );
});

export default FiltersPanel;
